import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, ArrowRight, Eye, EyeOff, Phone } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../hooks';
import { AuthShell, AuthButton, AuthAlert, authInputClass, authLabelClass } from '../components/AuthShell';

/**
 * When the admin toggles `feature_signup_open` to false in /admin/settings,
 * this page renders a "closed" panel instead of the form. Gate fetched
 * once on mount via /api/settings/public. While the flag is loading we
 * render the form optimistically — the server's /auth/register endpoint
 * is expected to own the final enforcement.
 */
const API_BASE =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL ?? 'http://localhost:3000';
const FRONTEND_KEY =
  (import.meta as unknown as { env: Record<string, string> }).env.VITE_FRONTEND_KEY ?? '';

const RegisterPage: React.FC = () => {
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/settings/public`, {
      headers: FRONTEND_KEY ? { 'X-Frontend-Key': FRONTEND_KEY } : {},
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((data: { featureSignupOpen?: boolean }) => {
        if (cancelled) return;
        setSignupOpen(data?.featureSignupOpen !== false);
      })
      .catch(() => {
        if (!cancelled) setSignupOpen(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    agreeToTerms: false, honeypot: '',
  });

  // Spam protection state
  const [formStartTime, setFormStartTime] = useState<number>(0);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const [interactionCount, setInteractionCount] = useState<number>(0);

  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError(null);
    if (isSubmitting) return;

    const currentTime = Date.now();
    const timeSinceStart = currentTime - formStartTime;
    const timeSinceLastSubmit = currentTime - lastSubmitTime;

    if (formData.honeypot) {
      setRegisterError('Please try again later');
      return;
    }
    if (timeSinceStart < 5000) {
      setRegisterError('Please take your time to fill out the form');
      return;
    }
    if (lastSubmitTime > 0 && timeSinceLastSubmit < 30000) {
      const remainingTime = Math.ceil((30000 - timeSinceLastSubmit) / 1000);
      setRegisterError(`Please wait ${remainingTime} seconds before trying again`);
      return;
    }
    if (interactionCount < 3) {
      setRegisterError('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    setLastSubmitTime(currentTime);

    try {
      if (formData.password !== formData.confirmPassword) {
        setRegisterError('Passwords do not match');
        return;
      }
      if (formData.password.length < 8) {
        setRegisterError('Password must be at least 8 characters long');
        return;
      }
      if (!formData.agreeToTerms) {
        setRegisterError('You must agree to the terms and conditions');
        return;
      }

      const result = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
      });

      if (result.success) setIsSuccess(true);
      else setRegisterError(result.error || 'Registration failed');
    } catch (error: any) {
      setRegisterError(error.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (name !== 'honeypot' && type !== 'checkbox') setInteractionCount((prev) => prev + 1);
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const footer =
    !isSuccess && signupOpen !== false ? (
      <>
        Already have an account?{' '}
        <Link to="/auth/login" className="font-semibold text-inkblack no-underline hover:text-inkblack">
          Sign in
        </Link>
      </>
    ) : undefined;

  return (
    <>
      <SEO title="Sign Up" description="Create your oddits account to get started" keywords="register, sign up" />

      <AuthShell
        title={isSuccess ? 'Check your email' : 'Create your account'}
        subtitle={isSuccess ? undefined : 'Start managing your practice in one calm place.'}
        footer={footer}
      >
        {signupOpen === false ? (
          <div className="space-y-4">
            <AuthAlert title="Signups are currently closed">
              New accounts aren&rsquo;t being accepted right now. If you have an
              invitation, use the link in your email. Otherwise, please check back later.
            </AuthAlert>
            <p className="text-center text-sm text-inkblack-60">
              <Link to="/auth/login" className="font-semibold text-inkblack no-underline hover:text-inkblack">
                Already have an account? Sign in
              </Link>
            </p>
          </div>
        ) : isSuccess ? (
          <div className="space-y-5">
            <AuthAlert tone="success" title="Account created">
              We&rsquo;ve sent a verification link to your email. Confirm it to activate your account, then sign in.
            </AuthAlert>
            <Link
              to="/auth/login"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-inkblack px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:-translate-y-0.5"
            >
              Return to sign in <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {registerError && <AuthAlert title="Registration failed" onClose={() => setRegisterError(null)}>{registerError}</AuthAlert>}

            <div className="space-y-1.5">
              <label htmlFor="name" className={authLabelClass}>Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                <input id="name" name="name" type="text" autoComplete="name" required value={formData.name} onChange={handleInputChange} className={authInputClass} placeholder="Your name" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="email" className={authLabelClass}>Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleInputChange} className={authInputClass} placeholder="you@professional.com" />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="phone" className={authLabelClass}>
                Phone number <span className="font-normal text-inkblack-60">(optional)</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                <input id="phone" name="phone" type="tel" autoComplete="tel" value={formData.phone} onChange={handleInputChange} className={authInputClass} placeholder="Your phone number" />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label htmlFor="password" className={authLabelClass}>Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                  <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.password} onChange={handleInputChange} className={authInputClass} placeholder="8+ characters" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-inkblack-60 hover:text-inkblack-60" aria-label="Toggle password">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className={authLabelClass}>Confirm</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
                  <input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.confirmPassword} onChange={handleInputChange} className={authInputClass} placeholder="Repeat password" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-inkblack-60 hover:text-inkblack-60" aria-label="Toggle password">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Honeypot — hidden from users, visible to bots */}
            <input name="honeypot" type="text" value={formData.honeypot} onChange={handleInputChange} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

            <label className="flex items-start gap-2 text-sm text-inkblack-60">
              <input id="agreeToTerms" name="agreeToTerms" type="checkbox" checked={formData.agreeToTerms} onChange={handleInputChange} className="mt-0.5 size-4 rounded border-receipt text-inkblack focus:ring-inkblack" />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-inkblack hover:underline">Terms</a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-inkblack hover:underline">Privacy Policy</a>.
              </span>
            </label>

            <AuthButton type="submit" loading={isSubmitting}>
              {isSubmitting ? 'Creating account…' : (<>Create account <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>)}
            </AuthButton>
          </form>
        )}
      </AuthShell>
    </>
  );
};

export default RegisterPage;
